import { create } from 'zustand';
import { VIEW_CONTEXT_LIMITS, type Speak2MeViewContext } from '@vivport/backend/types';

export type ViewContextSlots = {
  [K in keyof Omit<Speak2MeViewContext, 'path'>]?: NonNullable<Speak2MeViewContext[K]>;
};
export type ViewContextSlotName = keyof ViewContextSlots;
export type ViewContextFlush = () => Promise<void>;

interface ViewContextState {
  slots: ViewContextSlots;
  flushes: Map<string, ViewContextFlush>;
  setSlot: <K extends ViewContextSlotName>(name: K, value: NonNullable<ViewContextSlots[K]>) => void;
  clearSlot: (name: ViewContextSlotName) => void;
  registerFlush: (key: string, fn: ViewContextFlush) => void;
  unregisterFlush: (key: string) => void;
  flushAll: () => Promise<void>;
}

export const useViewContextStore = create<ViewContextState>((set, get) => ({
  slots: {},
  flushes: new Map(),
  setSlot: (name, value) => set((state) => ({ slots: { ...state.slots, [name]: value } })),
  clearSlot: (name) =>
    set((state) => {
      if (!(name in state.slots)) return state;
      const slots = { ...state.slots };
      delete slots[name];
      return { slots };
    }),
  registerFlush: (key, fn) =>
    set((state) => {
      const flushes = new Map(state.flushes);
      flushes.set(key, fn);
      return { flushes };
    }),
  unregisterFlush: (key) =>
    set((state) => {
      if (!state.flushes.has(key)) return state;
      const flushes = new Map(state.flushes);
      flushes.delete(key);
      return { flushes };
    }),
  flushAll: async () => {
    await Promise.all([...get().flushes.values()].map((fn) => fn()));
  },
}));

const L = VIEW_CONTEXT_LIMITS;

function cap(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) : text;
}

function buildExplore(
  explore: NonNullable<ViewContextSlots['explore']>
): Speak2MeViewContext['explore'] {
  const tab = explore.tab !== undefined && explore.tab.length > 0 ? cap(explore.tab, L.tab) : undefined;
  const query =
    explore.query !== undefined && explore.query.length > 0 ? cap(explore.query, L.query) : undefined;
  const selectedContentIds =
    explore.selectedContentIds !== undefined && explore.selectedContentIds.length > 0
      ? explore.selectedContentIds.slice(0, L.selection)
      : undefined;
  const slot: NonNullable<Speak2MeViewContext['explore']> = {
    ...(tab !== undefined ? { tab } : {}),
    ...(explore.savedFilterId !== undefined ? { savedFilterId: explore.savedFilterId } : {}),
    ...(query !== undefined ? { query } : {}),
    ...(selectedContentIds !== undefined ? { selectedContentIds } : {}),
  };
  return Object.keys(slot).length > 0 ? slot : undefined;
}

function buildMessageComposer(
  composer: NonNullable<ViewContextSlots['messageComposer']>
): NonNullable<Speak2MeViewContext['messageComposer']> {
  return {
    kind: composer.kind,
    ...(composer.draftContentId !== undefined ? { draftContentId: composer.draftContentId } : {}),
    ...(composer.sourceContentId !== undefined ? { sourceContentId: composer.sourceContentId } : {}),
    ...(composer.mode !== undefined ? { mode: composer.mode } : {}),
    ...(composer.unsaved !== undefined
      ? {
          unsaved: {
            to: cap(composer.unsaved.to, L.address),
            cc: cap(composer.unsaved.cc, L.address),
            bcc: cap(composer.unsaved.bcc, L.address),
            subject: cap(composer.unsaved.subject, L.subject),
            body: cap(composer.unsaved.body, L.unsavedBody),
          },
        }
      : {}),
  };
}

function buildEventComposer(
  composer: NonNullable<ViewContextSlots['eventComposer']>
): NonNullable<Speak2MeViewContext['eventComposer']> {
  return {
    title: cap(composer.title, L.eventTitle),
    startAt: cap(composer.startAt, L.eventTime),
    endAt: cap(composer.endAt, L.eventTime),
    isAllDay: composer.isAllDay,
    location: cap(composer.location, L.eventLocation),
    description: cap(composer.description, L.eventDescription),
    attendees: composer.attendees.slice(0, L.attendees).map((attendee) => cap(attendee, L.attendee)),
  };
}

export function buildSpeak2MeViewContext(pathname: string): Speak2MeViewContext {
  const { slots } = useViewContextStore.getState();
  const explore = slots.explore !== undefined ? buildExplore(slots.explore) : undefined;
  return {
    path: cap(pathname, L.path),
    ...(slots.content !== undefined
      ? {
          content: {
            code: cap(slots.content.code, L.code),
            ...(slots.content.sectionCode !== undefined
              ? { sectionCode: cap(slots.content.sectionCode, L.code) }
              : {}),
          },
        }
      : {}),
    ...(explore !== undefined ? { explore } : {}),
    ...(slots.calendar !== undefined
      ? { calendar: { weekStartIso: cap(slots.calendar.weekStartIso, L.weekStartIso) } }
      : {}),
    ...(slots.messageComposer !== undefined
      ? { messageComposer: buildMessageComposer(slots.messageComposer) }
      : {}),
    ...(slots.eventComposer !== undefined
      ? { eventComposer: buildEventComposer(slots.eventComposer) }
      : {}),
    ...(slots.documentEditor !== undefined
      ? {
          documentEditor: {
            sectionCode: cap(slots.documentEditor.sectionCode, L.code),
            unsavedText: cap(slots.documentEditor.unsavedText, L.unsavedText),
          },
        }
      : {}),
  };
}
